import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { formatNum, apiErr } from "../lib/utils";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "./ui/table";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
} from "recharts";
import { ArrowDownRight, ArrowUpRight, Minus, Sparkles, AlertTriangle, Database, Calculator } from "lucide-react";
import { toast } from "sonner";

function Movement({ pct, dir, lowerBetter }) {
  if (pct === null || pct === undefined) return <span className="text-muted-foreground text-xs">— vs PY</span>;
  const improving = lowerBetter ? pct < 0 : pct > 0;
  const Icon = dir === "down" ? ArrowDownRight : dir === "up" ? ArrowUpRight : Minus;
  const color = pct === 0 ? "text-muted-foreground" : improving ? "text-[#00E65B]" : "text-red-500";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`} data-testid="kpi-movement">
      <Icon className="h-3.5 w-3.5" />{pct > 0 ? "+" : ""}{pct}% <span className="text-muted-foreground font-normal">vs PY</span>
    </span>
  );
}

function buildSourceUrl(sf) {
  const p = new URLSearchParams({ range: "all" });
  if (sf.pillar) p.set("pillar", sf.pillar);
  if (sf.category) p.set("category", sf.category);
  if (sf.activity) p.set("activity", sf.activity);
  return `/data-management?${p.toString()}`;
}

export function KpiDrilldownDialog({ kpi, year, open, onClose, detailEndpoint, commentaryEndpoint, accent = "#00E65B" }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commentary, setCommentary] = useState("");
  const [genBusy, setGenBusy] = useState(false);

  useEffect(() => {
    if (!open || !kpi) return;
    setData(null); setCommentary("");
    setLoading(true);
    api.get(detailEndpoint(kpi.id, year))
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, [open, kpi, year, detailEndpoint]);

  const generate = async () => {
    if (!commentaryEndpoint) return;
    setGenBusy(true);
    try {
      const r = await api.post(commentaryEndpoint(kpi.id, year));
      setCommentary(r.data.commentary);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setGenBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="kpi-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {kpi?.name}
            <Badge variant="outline" className="text-[10px]">{kpi?.group}</Badge>
          </DialogTitle>
        </DialogHeader>

        {loading || !data ? (
          <div className="py-12 text-center text-muted-foreground font-mono text-sm">Loading KPI detail…</div>
        ) : (
          <Tabs defaultValue="overview" className="mt-2">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="overview" data-testid="dd-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="trend" data-testid="dd-tab-trend">Trend</TabsTrigger>
              <TabsTrigger value="source" data-testid="dd-tab-source">Source Data</TabsTrigger>
              <TabsTrigger value="anomalies" data-testid="dd-tab-anomalies">Anomalies</TabsTrigger>
              <TabsTrigger value="audit" data-testid="dd-tab-audit">Audit</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="card-flat p-3">
                  <div className="label-eyebrow text-[10px]">FY{year}</div>
                  <div className="font-mono text-xl font-bold">{data.overview.current != null ? formatNum(data.overview.current, { decimals: 1 }) : "—"} <span className="text-xs text-muted-foreground">{data.unit}</span></div>
                </div>
                <div className="card-flat p-3">
                  <div className="label-eyebrow text-[10px]">FY{year - 1}</div>
                  <div className="font-mono text-xl font-bold">{data.overview.previous != null ? formatNum(data.overview.previous, { decimals: 1 }) : "—"} <span className="text-xs text-muted-foreground">{data.unit}</span></div>
                </div>
                <div className="card-flat p-3">
                  <div className="label-eyebrow text-[10px]">Movement</div>
                  <div className="text-xl"><Movement pct={data.overview.movement_pct} dir={data.overview.movement_dir} lowerBetter={data.lower_is_better} /></div>
                </div>
              </div>

              <div className="card-flat p-4">
                <div className="label-eyebrow mb-1">Calculation method</div>
                <p className="text-sm text-muted-foreground">{data.calculation_method}</p>
              </div>

              {data.comparative?.target && (
                <div className="card-flat p-4">
                  <div className="label-eyebrow mb-2">Target trajectory</div>
                  <div className="text-sm font-medium">{data.comparative.target.name}</div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-2 mb-1">
                    <span>Baseline {data.comparative.target.baseline_year}: {formatNum(data.comparative.target.baseline_value)}</span>
                    <span>Target {data.comparative.target.target_year}: {formatNum(data.comparative.target.target_value)}</span>
                  </div>
                  <Progress value={data.comparative.target.progress_pct} className="h-2" />
                  <div className="text-xs mt-1 font-mono">{data.comparative.target.progress_pct}% to target {data.comparative.target.sbti_aligned && <Badge variant="outline" className="ml-2 text-[9px]">SBTi</Badge>}</div>
                </div>
              )}

              {commentaryEndpoint && (
                <div className="card-flat p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI performance commentary</div>
                    <Button size="sm" variant="primary" onClick={generate} disabled={genBusy} data-testid="dd-generate-commentary">
                      {genBusy ? "Generating…" : commentary ? "Regenerate" : "Generate"}
                    </Button>
                  </div>
                  {commentary
                    ? <p className="text-sm leading-relaxed" data-testid="dd-commentary-text">{commentary}</p>
                    : <p className="text-sm text-muted-foreground">Generate a plain-language explanation of this KPI's movement, likely drivers and a suggested action.</p>}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="card-flat p-3">
                  <div className="label-eyebrow text-[10px] mb-1">Framework alignment</div>
                  <div className="flex flex-wrap gap-1">
                    {data.frameworks.map((f) => <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>)}
                  </div>
                </div>
                <div className="card-flat p-3">
                  <div className="label-eyebrow text-[10px] mb-1">Data quality</div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="font-mono text-lg font-bold">{data.data_quality.score}</span>
                    <div className="text-muted-foreground">
                      <div>Complete {data.data_quality.completeness_pct}% · Verified {data.data_quality.verification_pct}%</div>
                      <div>Status: {data.data_quality.anomaly_status}</div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="trend" className="mt-4">
              <div className="card-flat p-4">
                <div className="label-eyebrow mb-3">{data.unit} by period</div>
                {data.trend.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={data.trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="ddGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={accent} stopOpacity={0.4} />
                          <stop offset="100%" stopColor={accent} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} tickFormatter={(p) => (p?.length > 5 ? p.slice(5) : p)} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <RTooltip />
                      <Area type="monotone" dataKey="value" stroke={accent} fill="url(#ddGrad)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <div className="text-sm text-muted-foreground py-8 text-center">No periodic series for this KPI.</div>}
              </div>
            </TabsContent>

            <TabsContent value="source" className="mt-4">
              {data.source_filter && (
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <p className="text-xs text-muted-foreground">The values below are captured in your source data table. Open it to manage, verify or add records for this measure.</p>
                  <Button variant="outline" size="sm" onClick={() => navigate(buildSourceUrl(data.source_filter))} data-testid="dd-source-link">
                    <Database className="h-3.5 w-3.5" /> View in source data table
                  </Button>
                </div>
              )}
              <div className="card-flat overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead><TableHead>Activity</TableHead>
                      <TableHead className="text-right">Value</TableHead><TableHead>Unit</TableHead>
                      <TableHead className="text-right">tCO₂e</TableHead><TableHead>Source</TableHead><TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.source_data.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{r.period}</TableCell>
                        <TableCell className="text-xs">
                          <span className="inline-flex items-center gap-1.5">
                            {r.activity}
                            {r.estimated && (
                              <Badge variant="warning" className="gap-1 text-[9px]" data-testid="source-estimated-badge"
                                title={r.method ? `Estimated — ${r.method} (derived, not metered)` : "Estimated (derived, not metered)"}>
                                <Calculator className="h-2.5 w-2.5" /> Est.
                              </Badge>
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">{formatNum(r.value)}</TableCell>
                        <TableCell className="text-xs">{r.unit}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{r.co2e_tonnes ?? "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{r.source || "—"}</TableCell>
                        <TableCell><Badge variant={r.status === "flagged" ? "destructive" : "outline"} className="text-[9px]">{r.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                    {!data.source_data.length && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6 text-sm">No source data.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="anomalies" className="mt-4 space-y-2">
              {data.anomalies.length ? data.anomalies.map((a, i) => (
                <div key={i} className="card-flat p-3 flex items-start gap-3">
                  <AlertTriangle className="h-4 w-4 text-[#FFB800] mt-0.5" />
                  <div>
                    <div className="text-sm font-medium">{a.activity} · <span className="font-mono">{a.period}</span></div>
                    <div className="text-xs text-muted-foreground">{a.note}</div>
                  </div>
                </div>
              )) : <div className="text-sm text-muted-foreground py-8 text-center">No open anomalies affecting this KPI.</div>}
            </TabsContent>

            <TabsContent value="audit" className="mt-4">
              <div className="space-y-1.5">
                {data.audit_trail.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs border-b border-border pb-1.5">
                    <span className="font-mono text-muted-foreground w-16">{a.period}</span>
                    <span className="flex-1">{a.action}</span>
                    <span className="text-muted-foreground">{a.actor}</span>
                  </div>
                ))}
                {!data.audit_trail.length && <div className="text-sm text-muted-foreground py-8 text-center">No audit entries.</div>}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
